import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Cpu,
  MapPin,
  MagnifyingGlass,
  Thermometer,
  Lightbulb,
  BatteryFull,
  CaretRight,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DevicePanel({
  datasetId,
  selectedDevice,
  onSelectDevice,
}) {
  const [devices, setDevices] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await axios.get(
          `${API}/devices?dataset_id=${datasetId}`
        );
        setDevices(res.data);
      } catch (e) {
        console.error("Failed to fetch devices", e);
      } finally {
        setLoading(false);
      }
    };
    fetchDevices();
  }, [datasetId]);

  const filteredDevices = devices.filter(
    (d) =>
      !search ||
      d.device_name?.toLowerCase().includes(search.toLowerCase()) ||
      d.address?.toLowerCase().includes(search.toLowerCase())
  );

  if (devices.length === 0 && !loading) return null;

  return (
    <div
      data-testid="device-panel"
      className="bg-zinc-900 border border-zinc-800 rounded-sm"
    >
      {/* Header */}
      <div className="p-3 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
            Devices
          </h3>
          <Badge variant="outline" className="text-xs font-mono border-zinc-700">
            {devices.length}
          </Badge>
        </div>
        <div className="relative">
          <MagnifyingGlass
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500"
          />
          <Input
            placeholder="Quick Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-teal-400"
            data-testid="device-search"
          />
        </div>
      </div>

      {/* All Devices option */}
      <div
        className={`px-3 py-2.5 border-b border-zinc-800 cursor-pointer transition-colors duration-150 ${
          !selectedDevice
            ? "bg-teal-500/10 border-l-2 border-l-teal-400"
            : "hover:bg-zinc-800/50"
        }`}
        onClick={() => onSelectDevice(null)}
        data-testid="device-all-btn"
      >
        <div className="flex items-center gap-2">
          <Cpu size={16} weight="duotone" className="text-teal-400" />
          <span className="text-xs font-semibold text-zinc-200">
            All Devices
          </span>
          <span className="text-[10px] font-mono text-zinc-500 ml-auto">
            {devices.reduce((s, d) => s + (d.event_count || 0), 0)} events
          </span>
        </div>
      </div>

      {/* Device list */}
      <ScrollArea className="max-h-[400px]">
        {loading ? (
          <div className="p-4 text-xs text-zinc-500 font-mono animate-pulse">
            Loading devices...
          </div>
        ) : (
          filteredDevices.map((device) => {
            const isSelected = selectedDevice === device.device_name;
            const hasLight =
              device.light &&
              device.light.toLowerCase() !== "dark";
            return (
              <div
                key={device.device_name}
                className={`px-3 py-3 border-b border-zinc-800/50 cursor-pointer transition-colors duration-150 ${
                  isSelected
                    ? "bg-teal-500/10 border-l-2 border-l-teal-400"
                    : "hover:bg-zinc-800/50"
                }`}
                onClick={() => onSelectDevice(device.device_name)}
                data-testid={`device-card-${device.device_name}`}
              >
                {/* Device name row */}
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        hasLight ? "bg-amber-400" : "bg-emerald-400"
                      }`}
                    />
                    <span className="text-sm font-semibold text-zinc-100">
                      {device.device_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {device.temperature != null && (
                      <span className="text-[10px] font-mono text-zinc-400 flex items-center gap-0.5">
                        <Thermometer size={10} />
                        {device.temperature}\u00b0F
                      </span>
                    )}
                    {device.battery_level != null && (
                      <span className="text-[10px] font-mono text-zinc-400 flex items-center gap-0.5">
                        <BatteryFull size={10} />
                        {device.battery_level}%
                      </span>
                    )}
                    <CaretRight
                      size={12}
                      className="text-zinc-600 ml-1"
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-1.5 mb-1.5">
                  <MapPin
                    size={11}
                    className="text-zinc-500 mt-0.5 shrink-0"
                  />
                  <p className="text-[10px] text-zinc-500 leading-tight truncate">
                    {device.address || "Unknown location"}
                  </p>
                </div>

                {/* Status bar */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono text-teal-500">
                    {device.event_count} events
                  </span>
                  <div className="flex items-center gap-1">
                    <Lightbulb
                      size={10}
                      className={
                        hasLight ? "text-amber-400" : "text-zinc-600"
                      }
                    />
                    <span
                      className={`text-[10px] font-mono ${
                        hasLight ? "text-amber-400" : "text-zinc-600"
                      }`}
                    >
                      {device.light || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </ScrollArea>
    </div>
  );
}
